import { useState, useEffect, useRef } from 'react';
import InputField from '../../../components/Forms/Input/InputField';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import SvgIcon from '../../SvgIcon/SvgIcon';
import { ivaTypeOptions } from '../productData';
import SelectField from '../../Forms/Input/SelectField';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { useExcludedSubject } from '../../../hooks/purchases/useExcludedSubject';
import { useEnterToClick } from '../../../hooks/useEnter';
import { useProduct } from '../../../hooks/useProduct';
import { ITEM_TYPE_CODES } from '../../../constants/itemTypeCodes';

const AddExcludedServiceForm = ({
  onAdd,
}: {
  onAdd: (service: any) => void;
}) => {
  const saveBtnRef = useRef<HTMLButtonElement>(null);
  useEnterToClick(saveBtnRef, true);

  // Productos (servicios) desde hook
  const { productsDropdown, handlerGetProductsForDropdownByItemType } =
    useProduct();

  const {
    form: { id: purchaseId },
  } = useSelector((state: RootState) => state.basePurchase);

  const serviceToEdit = useSelector(
    (state: RootState) => state.excludedSubject.serviceToEdit
  );

  const { clearServiceToEdit, handleCancelService, handlerUpdateProduct } =
    useExcludedSubject();

  // ─────────────────────────────
  // Estado del formulario
  // ─────────────────────────────
  const [form, setForm] = useState({
    detailId: null as number | null,
    productId: '',
    productName: '',
    ivaType: 'EXENTO',
    // calculadora
    calculatedTotal: '',
    calculatedQty: '',
    // manual/directo
    unitCost: '',
    quantity: '',
    // derivados
    totalCost: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Flags para controlar entradas manuales
  const [isManualUnitCost, setIsManualUnitCost] = useState(false);
  const [isManualQuantity, setIsManualQuantity] = useState(false);
  const isEditForm = !!form.detailId; 

  // Cargar servicios al abrir
  useEffect(() => {
    handlerGetProductsForDropdownByItemType(ITEM_TYPE_CODES.SERVICES);
  }, []);

  // Precargar edición
  useEffect(() => {
    if (serviceToEdit) {
      setForm({
        detailId: serviceToEdit.detailId,
        productId: String(serviceToEdit.productId || ''),
        productName: serviceToEdit.productName || '',
        ivaType: 'EXENTO',
        calculatedTotal: '',
        calculatedQty: '',
        unitCost: String(serviceToEdit.unitCost ?? ''),
        quantity: String(serviceToEdit.quantity ?? ''),
        totalCost: (
          Number(serviceToEdit.unitCost) * Number(serviceToEdit.quantity)
        ).toFixed(2),
      });
      setIsManualUnitCost(false);
      setIsManualQuantity(true); // viene con cantidad directa
    }
  }, [serviceToEdit]);

  // Cambios controlados
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'unitCost') {
      setIsManualUnitCost(true);
      setForm((prev) => ({ ...prev, unitCost: value }));
      return;
    }

    if (name === 'quantity') {
      setIsManualQuantity(true);
      setForm((prev) => ({
        ...prev,
        quantity: value,
        calculatedQty: '', // anula calculadora
      }));
      return;
    }

    if (name === 'calculatedQty') {
      setIsManualQuantity(false);
      setForm((prev) => ({
        ...prev,
        calculatedQty: value,
        quantity: '', // anula manual
      }));
      return;
    }

    if (name === 'calculatedTotal') {
      setForm((prev) => ({ ...prev, calculatedTotal: value }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Cálculos EXENTO (sin IVA)
  useEffect(() => {
    const { calculatedTotal, calculatedQty, quantity, unitCost } = form;

    // 1) Si hay Total (calc) + alguna Cantidad (manual o calc)
    //    y unitCost NO fue tocado manualmente → derivar unitCost
    if (calculatedTotal && (calculatedQty || quantity) && !isManualUnitCost) {
      const total = parseFloat(calculatedTotal || '0');
      const qty = parseFloat(quantity || calculatedQty || '0');

      if (isFinite(total) && isFinite(qty) && total > 0 && qty > 0) {
        const u = total / qty;
        setForm((prev) => ({
          ...prev,
          unitCost: u.toFixed(8),
          totalCost: total.toFixed(2),
        }));
      }
    }

    // 2) Unitario + Cantidad → Total (traiga de manual o calculadora)
    const qty2 = parseFloat(
      (isManualQuantity ? form.quantity : form.calculatedQty) || '0'
    );
    const u2 = parseFloat(unitCost || '0');

    if (isFinite(u2) && isFinite(qty2) && u2 > 0 && qty2 > 0) {
      const total2 = u2 * qty2;
      setForm((prev) => ({
        ...prev,
        totalCost: total2.toFixed(2),
      }));
    }
  }, [
    form.calculatedTotal,
    form.calculatedQty,
    form.unitCost,
    form.quantity,
    isManualUnitCost,
    isManualQuantity,
  ]);

  const handleAdd = () => {
    const errs: typeof errors = {};
    if (!form.productId) errs.productId = 'Servicio requerido';
    if (!form.unitCost) errs.unitCost = 'Costo requerido';
    if (!form.quantity && !form.calculatedQty)
      errs.quantity = 'Cantidad requerida';

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    if (form.detailId) {
      // Editar
      const updatePayload = {
        detailId: form.detailId!,
        productId: Number(form.productId),
        quantity: parseFloat(form.quantity || form.calculatedQty),
        unitCost: parseFloat(form.unitCost),
        taxType: 'EXEMPT',
      };
      handlerUpdateProduct(updatePayload);
    } else {
      // Crear
      const createPayload = {
        purchaseId,
        productId: Number(form.productId),
        productName: form.productName,
        quantity: parseFloat(form.quantity || form.calculatedQty),
        unitCost: parseFloat(form.unitCost),
        totalAmount: parseFloat(form.totalCost || '0'),
        taxType: 'EXEMPT',
      };
      onAdd(createPayload);
    }
  };

  useEffect(() => {
    return () => {
      clearServiceToEdit();
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Servicio + Tipo */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <SimpleSearchSelect
          label="Servicio"
          name="productId"
          iconKey="category"
          options={productsDropdown}
          selectedValue={form.productId ? Number(form.productId) : null}
          onSelect={(option: any) =>
            setForm((prev) => ({
              ...prev,
              productId: option.id,
              productName: option.name,
            }))
          }
          error={errors.productId}
          disabled={isEditForm}
          required
        />

        <SelectField
          label="Tipo"
          name="ivaType"
          iconKey="list"
          value={form.ivaType}
          onChange={() => {}}
          options={ivaTypeOptions}
          disabled
          required
        />
      </div>

      {/* Unitario / Cantidad / Total directo */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <InputField
          label="Costo Unitario"
          name="unitCost"
          value={form.unitCost}
          onChange={handleChange}
          iconKey="dollar"
          type="number"
          error={errors.unitCost}
          min={0}
          required
        />
        <InputField
          label="Cantidad"
          name="quantity"
          value={form.quantity}
          onChange={handleChange}
          iconKey="product"
          type="number"
          error={errors.quantity}
          min={0}
        />
        <InputField
          label="Costo Total"
          name="totalCost"
          iconKey="dollar"
          type="number"
          value={form.totalCost}
          onChange={() => {}}
          disabled
        />
      </div>

      {/* Calculadora (Total + Cantidad) → Unitario */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <InputField
          label="Costo Total (Calculadora)"
          name="calculatedTotal"
          iconKey="dollar"
          type="number"
          value={form.calculatedTotal}
          onChange={handleChange}
        />
        <InputField
          label="Cantidad (Calculadora)"
          name="calculatedQty"
          iconKey="product"
          type="number"
          value={form.calculatedQty}
          onChange={handleChange}
        />
        <div />
      </div>

      {/* Botones */}
      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          ref={saveBtnRef}
          onClick={handleAdd}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          <SvgIcon name="save" className="h-5 w-5" />
          Guardar
        </button>
        <button
          type="button"
          onClick={handleCancelService}
          className="inline-flex items-center gap-2 rounded-md bg-[#DC3545] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          <SvgIcon name="cancel" className="h-5 w-5" />
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default AddExcludedServiceForm;
